// web/src/pages/Community.tsx
import type { JSX, ReactNode } from "react";
import { Link } from "react-router-dom";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Button } from "@/components/ui/button";

const EFFECTIVE_DATE_TEXT = "August 21, 2025";
const LAST_UPDATED_TEXT = "August 21, 2025";
const SUPPORT_EMAIL = "support@spellgather.com";

function Section({
  id,
  title,
  children,
}: {
  id: string;
  title: string;
  children: ReactNode;
}): JSX.Element {
  return (
    <section id={id} className="scroll-mt-24">
      <h2 className="text-xl sm:text-2xl font-semibold text-white mt-8 mb-3">
        {title}
      </h2>
      <div className="prose prose-invert max-w-none">{children}</div>
    </section>
  );
}

export default function Community(): JSX.Element {
  return (
    <main className="mx-auto max-w-4xl p-6">
      <Card className="bg-gray-900 border-gray-700 shadow-xl">
        <CardHeader>
          <CardTitle className="text-white">Community Guidelines</CardTitle>
          <CardDescription className="text-white/90">
            Effective: {EFFECTIVE_DATE_TEXT} · Last Updated: {LAST_UPDATED_TEXT}
          </CardDescription>
        </CardHeader>

        <CardContent className="text-white">
          {/* TOC */}
          <nav aria-label="Table of contents" className="mb-6">
            <ul className="text-sm grid sm:grid-cols-2 gap-2">
              <li>
                <a className="underline" href="#overview">
                  1. Overview & Scope
                </a>
              </li>
              <li>
                <a className="underline" href="#principles">
                  2. Community Principles
                </a>
              </li>
              <li>
                <a className="underline" href="#conduct">
                  3. Conduct Rules
                </a>
              </li>
              <li>
                <a className="underline" href="#content">
                  4. Content Standards
                </a>
              </li>
              <li>
                <a className="underline" href="#trading">
                  5. Trading & Offers Etiquette
                </a>
              </li>
              <li>
                <a className="underline" href="#minors">
                  6. Youth Safety (13–17)
                </a>
              </li>
              <li>
                <a className="underline" href="#privacy">
                  7. Privacy & Data
                </a>
              </li>
              <li>
                <a className="underline" href="#reporting">
                  8. Reporting & Appeals
                </a>
              </li>
              <li>
                <a className="underline" href="#enforcement">
                  9. Enforcement Ladder
                </a>
              </li>
              <li>
                <a className="underline" href="#names">
                  10. Profiles, Names & Impersonation
                </a>
              </li>
              <li>
                <a className="underline" href="#dm">
                  11. Messaging & Anti-Spam
                </a>
              </li>
              <li>
                <a className="underline" href="#ip">
                  12. Intellectual Property
                </a>
              </li>
              <li>
                <a className="underline" href="#changes">
                  13. Changes to These Guidelines
                </a>
              </li>
              <li>
                <a className="underline" href="#contact">
                  Contact
                </a>
              </li>
            </ul>
          </nav>

          <Section id="overview" title="1) Overview & Scope">
            <p>
              Spellgather is a community for collectors and players to discover,
              connect, and trade responsibly. These Community Guidelines
              (“Guidelines”) apply to all activity on the Service, including
              public profiles, collections, offers, messages, and follows. They
              work alongside our{" "}
              <Link to="/terms" className="underline">
                Terms of Service
              </Link>
              ,{" "}
              <Link to="/privacy" className="underline">
                Privacy Policy
              </Link>
              , and{" "}
              <Link to="/data-protection" className="underline">
                Data Sharing & Protection Statement
              </Link>
              .
            </p>
          </Section>

          <Section id="principles" title="2) Community Principles">
            <ul>
              <li>
                <strong>Play fair:</strong> Be honest, keep commitments, and
                respect others’ time.
              </li>
              <li>
                <strong>Be kind:</strong> No harassment, hate, or personal
                attacks.
              </li>
              <li>
                <strong>Stay safe:</strong> Protect your privacy; use built-in
                tools to manage visibility.
              </li>
              <li>
                <strong>Respect rights:</strong> Don’t infringe IP or
                misrepresent items.
              </li>
              <li>
                <strong>Welcome youth:</strong> Teens (13–17) may participate
                with guardian oversight; help keep the space age-appropriate.
              </li>
            </ul>
          </Section>

          <Section id="conduct" title="3) Conduct Rules">
            <ul>
              <li>
                <strong>No harassment or hate:</strong> Slurs, demeaning
                stereotypes, or attacks based on protected classes are
                prohibited.
              </li>
              <li>
                <strong>No threats or violence:</strong> Threats, doxxing, or
                encouragement of self-harm/violence are not allowed.
              </li>
              <li>
                <strong>No sexual content involving minors:</strong> Strictly
                prohibited. Art or discussion sexualizing minors is not allowed.
              </li>
              <li>
                <strong>No illegal activity:</strong> Fraud, fencing stolen
                goods, money laundering, or facilitating illegal trades is
                banned.
              </li>
              <li>
                <strong>No scams:</strong> Phishing, impersonation, fake escrow,
                payment redirection, or off-platform “too good to be true”
                offers.
              </li>
              <li>
                <strong>Respect boundaries:</strong> If someone asks you to stop
                contacting them, stop.
              </li>
              <li>
                <strong>Keep it relevant:</strong> Avoid spam or irrelevant
                promotions.
              </li>
            </ul>
          </Section>

          <Section id="content" title="4) Content Standards">
            <ul>
              <li>
                <strong>Accuracy:</strong> Describe conditions (e.g.,
                NM/LP/MP/HP/DMG) and foiling truthfully.
              </li>
              <li>
                <strong>No counterfeit:</strong> Counterfeits, proxies
                represented as genuine, or tampered items are not allowed.
              </li>
              <li>
                <strong>Images:</strong> No graphic violence, pornography, or
                hateful symbols.
              </li>
              <li>
                <strong>Respect IP:</strong> Only upload images you have rights
                to share or that are permitted by fair use. Comply with takedown
                requests.
              </li>
              <li>
                <strong>Private info:</strong> Do not post someone else’s
                private or identifying info without consent.
              </li>
            </ul>
          </Section>

          <Section id="trading" title="5) Trading & Offers Etiquette">
            <p className="mb-2">
              Trades/offers are user-to-user. Spellgather is not a party to
              transactions. Use common-sense safety:
            </p>
            <ul>
              <li>
                <strong>Transparency:</strong> Disclose defects, alters, or play
                wear clearly.
              </li>
              <li>
                <strong>Proof & references:</strong> Consider sharing
                timestamped photos and references for higher-value trades.
              </li>
              <li>
                <strong>Meetups:</strong> Prefer public locations (e.g., your
                preferred LGS). Take a friend for high-value exchanges.
              </li>
              <li>
                <strong>Shipping:</strong> Use tracked/insured options when
                applicable; agree on who pays shipping before finalizing.
              </li>
              <li>
                <strong>No off-platform pressure:</strong> Don’t push users to
                risky payment methods or rush them.
              </li>
              <li>
                <strong>Honor commitments:</strong> If you accept an offer,
                follow through or communicate promptly if something changes.
              </li>
            </ul>
          </Section>

          <Section id="minors" title="6) Youth Safety (13–17)">
            <p>
              Users aged 13–17 may use the Service only with parent/guardian
              consent and supervision. Do not solicit personal contact details
              from minors, and keep all interactions age-appropriate. Report any
              concerning behavior immediately to{" "}
              <a className="underline" href={`mailto:${SUPPORT_EMAIL}`}>
                {SUPPORT_EMAIL}
              </a>
              .
            </p>
          </Section>

          <Section id="privacy" title="7) Privacy & Data">
            <p>
              Review your visibility controls in{" "}
              <Link to="/settings" className="underline">
                Settings
              </Link>{" "}
              (e.g., location, meetup preferences, collection browsing,
              wishlist). Our handling of personal data is described in our{" "}
              <Link to="/privacy" className="underline">
                Privacy Policy
              </Link>
              . For transparency, items that remain within Spellgather may show
              non-sensitive history markers (e.g., “added to collection,”
              “traded to @user” with timestamps).
            </p>
          </Section>

          <Section id="reporting" title="8) Reporting & Appeals">
            <p className="mb-2">
              If you encounter harassment, scams, IP infringement, or unsafe
              behavior:
            </p>
            <ul>
              <li>
                Use in-app controls (unfollow, stop messaging, adjust
                visibility, block as available).
              </li>
              <li>
                Email{" "}
                <a
                  className="underline"
                  href={`mailto:${SUPPORT_EMAIL}?subject=Report%20Community%20Issue`}
                >
                  {SUPPORT_EMAIL}
                </a>{" "}
                with details (usernames, links, screenshots, timestamps).
              </li>
              <li>
                For IP concerns, include evidence of rights and the specific
                content URLs.
              </li>
            </ul>
            <p className="mt-2">
              Appeals can be submitted to the same address with the subject line
              “Appeal”. Provide context and any corrections you have made.
            </p>
          </Section>

          <Section id="enforcement" title="9) Enforcement Ladder">
            <p className="mb-2">
              We apply proportionate actions based on severity and history:
            </p>
            <ol>
              <li>Education or warning.</li>
              <li>
                Feature limits (e.g., messaging or offers) or temporary
                visibility restrictions.
              </li>
              <li>Temporary suspension.</li>
              <li>
                Permanent account ban and, if applicable, removal of violating
                content.
              </li>
            </ol>
            <p className="mt-2">
              Severe violations (e.g., threats, child safety issues, fraud) may
              lead to immediate suspension and referral to appropriate
              authorities.
            </p>
          </Section>

          <Section id="names" title="10) Profiles, Names & Impersonation">
            <ul>
              <li>
                <strong>Accurate identity:</strong> Do not impersonate people or
                organizations.
              </li>
              <li>
                <strong>Usernames:</strong> Must comply with these Guidelines
                (no hate, slurs, or targeted harassment).
              </li>
              <li>
                <strong>Avatars & bios:</strong> Keep images and text
                appropriate and non-deceptive.
              </li>
            </ul>
          </Section>

          <Section id="dm" title="11) Messaging & Anti-Spam">
            <ul>
              <li>
                No bulk or automated messaging. No unsolicited commercial
                messages.
              </li>
              <li>
                Do not share malicious links or attempt credential harvesting.
              </li>
              <li>
                Respect “no contact” requests and local laws governing
                communications.
              </li>
            </ul>
          </Section>

          <Section id="ip" title="12) Intellectual Property">
            <p>
              Only upload/share material you have rights to. We respond to IP
              takedown requests. Report alleged infringement to{" "}
              <a
                className="underline"
                href={`mailto:${SUPPORT_EMAIL}?subject=IP%20Takedown%20Request`}
              >
                {SUPPORT_EMAIL}
              </a>{" "}
              with: (1) your contact info; (2) work identified; (3) allegedly
              infringing URLs; (4) a statement of good-faith belief; and (5)
              your signature (typed is acceptable). We may notify the user and
              remove or restrict content as appropriate.
            </p>
          </Section>

          <Section id="changes" title="13) Changes to These Guidelines">
            <p>
              We may update these Guidelines to reflect new features or
              requirements. Material changes will be announced in-app or by
              email when appropriate. Continued use after the effective date
              constitutes acceptance.
            </p>
          </Section>

          <Section id="contact" title="Contact">
            <p>
              Need help or to report a concern? Contact{" "}
              <a className="underline" href={`mailto:${SUPPORT_EMAIL}`}>
                {SUPPORT_EMAIL}
              </a>
              .
            </p>
            <p className="mt-2">
              Also see our{" "}
              <Link to="/terms" className="underline">
                Terms of Service
              </Link>
              ,{" "}
              <Link to="/privacy" className="underline">
                Privacy Policy
              </Link>
              , and{" "}
              <Link to="/data-protection" className="underline">
                Data Sharing & Protection Statement
              </Link>
              .
            </p>
          </Section>

          {/* Convenience CTAs */}
          <div className="mt-8 flex flex-wrap gap-2">
            <Link to="/settings">
              <Button className="bg-purple-600 hover:bg-purple-700">
                Open Settings
              </Button>
            </Link>
            <a href={`mailto:${SUPPORT_EMAIL}?subject=Community%20Report`}>
              <Button
                variant="outline"
                className="border-slate-600 text-slate-200 hover:bg-slate-800"
              >
                Email Support
              </Button>
            </a>
            <Link to="/terms-ack?next=/settings">
              <Button
                variant="outline"
                className="border-slate-600 text-slate-200 hover:bg-slate-800"
              >
                Return to Acknowledgement
              </Button>
            </Link>
          </div>
        </CardContent>
      </Card>
    </main>
  );
}

